import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth, useClerk, useSignIn } from "@clerk/react";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const CLASSIC = "/accesso-classico";
const IT = { form_password_incorrect: "Password errata. Riprova o usa «Password dimenticata».", form_identifier_not_found: "Nessun account con questa email.",
  form_code_incorrect: "Codice errato.", verification_expired: "Codice scaduto: chiedine uno nuovo.", too_many_requests: "Troppi tentativi: attendi qualche minuto." };
const TEST_CODE = "424242";  // Clerk test mode: accepted only by DEVELOPMENT instances, only for +clerk_test addresses (no email is sent)
const qaAddress = (clerk, email) => clerk?.instanceType === "development" && /\+clerk_test@/i.test(email);
const msg = (e) => IT[e?.code] || IT[e?.errors?.[0]?.code] || e?.longMessage || e?.message || "Accesso non riuscito";

// Same-origin targets only (no open redirect); invites pass an absolute URL of this site.
export function safeTarget() {
  const r = new URLSearchParams(window.location.search).get("redirect_url");
  try { const u = new URL(r || "/", window.location.origin); return u.origin === window.location.origin ? u.pathname + u.search + u.hash : "/"; } catch { return "/"; }
}

function CodeStep({ signIn, factor, busy, onDone }) {
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    const fn = { email_code: signIn.mfa.verifyEmailCode, totp: signIn.mfa.verifyTOTP, backup_code: signIn.mfa.verifyBackupCode }[factor];
    const { error } = await fn({ code: code.trim() });
    if (error) return setErr(msg(error));
    if (signIn.status === "complete") return onDone();
    setErr("Verifica non completata: usa l'accesso classico.");
  };
  const resend = async () => { const { error } = await signIn.mfa.sendEmailCode(); setErr(error ? msg(error) : "Nuovo codice inviato."); };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="quick-signin-code-form">
      <p className="text-sm text-slate-600">{factor === "email_code" ? "Per sicurezza ti abbiamo inviato un codice via email: inseriscilo per completare l'accesso." : factor === "totp" ? "Inserisci il codice della tua app di autenticazione." : "Inserisci un codice di backup."}</p>
      <div><Label htmlFor="otp">Codice</Label><Input id="otp" name="otp" inputMode="numeric" autoComplete="one-time-code" autoFocus value={code} onChange={(e) => setCode(e.target.value)} required data-testid="quick-signin-code" /></div>
      <Button type="submit" className="w-full bg-sky-600 hover:bg-sky-700" disabled={busy || !code.trim()} data-testid="quick-signin-verify">{busy && <Loader2 size={15} className="mr-1.5 animate-spin" />}Verifica</Button>
      {factor === "email_code" && <button type="button" onClick={resend} className="text-xs text-sky-700 hover:underline" data-testid="quick-signin-resend">Invia un nuovo codice</button>}
      {err && <p role="alert" className="text-sm text-red-600" data-testid="quick-signin-error">{err}</p>}
    </form>);
}

// One-step sign-in (email + password on the first screen, for password managers) built on Clerk's official custom-flow API:
// Clerk still verifies password, MFA and new-device codes; anything else goes to the classic Clerk screen.
export default function QuickSignIn() {
  const { isSignedIn, isLoaded } = useAuth();
  const { signIn, fetchStatus } = useSignIn();
  const clerk = useClerk();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [factor, setFactor] = useState(null);
  const [queued, setQueued] = useState(false);
  const busy = fetchStatus === "fetching" || queued;
  const target = safeTarget();
  const classic = `${CLASSIC}${window.location.search}`;
  useEffect(() => { if (queued && isLoaded) { setQueued(false); run(); } });  // autofill + Enter before Clerk loaded: submit as soon as ready
  const done = async () => { const { error } = await signIn.finalize({ navigate: ({ decorateUrl }) => { window.location.assign(decorateUrl(target)); } }); if (error) setErr(msg(error)); };
  const pickSecond = async () => {
    const f = (signIn.supportedSecondFactors || []).map((x) => x.strategy);
    const s = f.includes("email_code") && (signIn.status === "needs_client_trust" || !f.includes("totp")) ? "email_code" : f.includes("totp") ? "totp" : f.includes("backup_code") ? "backup_code" : null;
    if (!s) return window.location.assign(classic);
    if (s === "email_code") {
      const { error } = await signIn.mfa.sendEmailCode();
      if (error) return setErr(msg(error));
      if (qaAddress(clerk, email.trim())) {  // DEV synthetic QA account: Clerk still verifies the fixed test code server-side
        const r = await signIn.mfa.verifyEmailCode({ code: TEST_CODE });
        if (!r.error && signIn.status === "complete") return done();
        setErr(`Accesso QA automatico non riuscito${r.error ? `: ${msg(r.error)}` : ""}. Inserisci il codice di test manualmente.`);
      }
    }
    setFactor(s);
  };
  const run = async () => {
    setErr("");
    const { error } = await signIn.password({ emailAddress: email.trim(), password });
    if (error) return setErr(msg(error));
    if (signIn.status === "complete") return done();
    if (signIn.status === "needs_second_factor" || signIn.status === "needs_client_trust") return pickSecond();
    window.location.assign(classic);  // needs_new_password / needs_first_factor / anything new: Clerk's own screens
  };
  const submit = (e) => { e.preventDefault(); setShow(false); if (!email.trim() || !password) return setErr("Inserisci email e password"); if (!isLoaded) return setQueued(true); run(); };
  if (isLoaded && isSignedIn && !factor) return <Navigate to={target} replace />;
  return (
    <div className="w-full max-w-sm rounded-xl border bg-white p-7 shadow-sm" data-testid="quick-signin">
      <h2 className="text-xl font-semibold text-slate-900">Accedi a MUSORA</h2>
      <p className="mb-5 mt-1 text-sm text-slate-500">Bentornato! Inserisci email e password.</p>
      {factor ? <CodeStep signIn={signIn} factor={factor} busy={busy} onDone={done} /> : (
        <form onSubmit={submit} className="space-y-3" method="post" action="#" data-testid="quick-signin-form">
          <div><Label htmlFor="username">Email</Label><Input id="username" name="username" type="email" autoComplete="username" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="quick-signin-email" /></div>
          <div><Label htmlFor="password">Password</Label>
            <div className="relative">
              <Input id="password" name="password" type={show ? "text" : "password"} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required className="pr-10" data-testid="quick-signin-password" />
              <button type="button" onClick={() => setShow((v) => !v)} aria-label={show ? "Nascondi password" : "Mostra password"} aria-pressed={show} aria-controls="password" title={show ? "Nascondi password" : "Mostra password"}
                className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-md text-slate-500 hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500" data-testid="quick-signin-toggle-password">
                {show ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div></div>
          <Button type="submit" className="h-11 w-full bg-sky-600 hover:bg-sky-700 sm:h-9" disabled={busy} data-testid="quick-signin-submit">{busy && <Loader2 size={15} className="mr-1.5 animate-spin" />}Accedi</Button>
          {err && <p role="alert" className="text-sm text-red-600" data-testid="quick-signin-error">{err}</p>}
        </form>)}
      <div className="mt-5 flex flex-wrap justify-between gap-2 border-t pt-4 text-xs">
        <a href={classic} className="text-sky-700 hover:underline" data-testid="quick-signin-forgot">Password dimenticata?</a>
        <a href={classic} className="text-slate-500 hover:underline" data-testid="quick-signin-classic">Altri metodi di accesso</a>
      </div>
    </div>);
}
